import React, {useContext} from 'react';
import {Link, useLocation, useHistory} from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Grid,
    AppBar,
    Divider,
    Drawer,
    Hidden,
    IconButton,
    List,
    Toolbar,
} from '@material-ui/core';
import withWidth, {isWidthUp} from '@material-ui/core/withWidth';
import {useTheme, makeStyles} from '@material-ui/core/styles';
import AssignmentTurnedInOutlined from '@material-ui/icons/AssignmentTurnedInOutlined';
import DvrIcon from '@material-ui/icons/Dvr';
import Brightness4Outlined from '@material-ui/icons/Brightness4Outlined';
import Brightness4 from '@material-ui/icons/Brightness4';
import FolderOutlined from '@material-ui/icons/FolderOutlined';
import SettingsOutlined from '@material-ui/icons/SettingsOutlined';
import MenuIcon from '@material-ui/icons/Menu';
import ClearRounded from '@material-ui/icons/ClearRounded';
import SupervisorAccount from '@material-ui/icons/SupervisorAccount';
import AccountBalance from '@material-ui/icons/AccountBalance';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import FaceIcon from '@material-ui/icons/Face';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import {DirectionsBoat} from '@material-ui/icons';
import {ListItemLink, ListItemButton, Greeting, FixedSupport} from 'components';
import {Logo, AnchorGray, LogoText} from 'components/icons/icons';
import {AuthContext} from 'context/AuthContext';
import {UserContext} from 'context/UserContext';
import {AppCtx} from 'context/AppContext';
import {useFlags} from 'hooks';
import PortalNavItems from './PortalNavItems';
import Show from 'components/utils/Show';
import {OnboardContent} from 'pages/Onboard';
import {drawerWidth} from 'components/ResponsiveNav';

const iconSize = '24px';
const iconProps = {height: iconSize, width: iconSize};

const useNavStyles = makeStyles(theme => ({
    drawer: {
        [theme.breakpoints.up('lg')]: {
            width: drawerWidth,
            flexShrink: 0,
        },
    },
    appBar: {
        [theme.breakpoints.up('lg')]: {
            width: `calc(100% - ${drawerWidth})`,
            marginLeft: drawerWidth,
        },
    },
    menuButton: {
        marginRight: theme.spacing(2),
        [theme.breakpoints.up('lg')]: {
            display: 'none',
        },
    },
    // necessary for content to be below app bar
    toolbar: theme.mixins.toolbar,
    drawerPaper: {
        width: drawerWidth,
        textAlign: 'center',
        overflowX: 'hidden',
    },
    mainBar: {
        paddingRight: '0',
    },
}));

const NavItemList = ({itemList, name = 'list', ...componentProps}) => {
    const location = useLocation();
    return [
        <List key={name}>
            {itemList?.map(item => {
                const {component, ...itemProps} = item;
                const Component = component || ListItemLink;
                if (item.name !== 'deactivated') {
                    return (
                        <Component
                            {...itemProps}
                            primary={item.name}
                            key={item.name}
                            selected={
                                //selectedIndex === item.name ||
                                item.to === location.pathname
                            }
                            {...componentProps}
                        />
                    );
                } else {
                    return null;
                }
            })}
        </List>,
        <Divider key={`${name}-divider`} />,
    ];
};

const LoggedInNavBar = ({width, ...props}) => {
    const history = useHistory();
    const local = window.location.hostname === 'localhost';
    const classes = useNavStyles();
    const theme = useTheme();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const {logOut} = useContext(AuthContext);
    const {
        user: {permissions},
        subscription: {subscription_type: subType},
        togglePro,
    } = useContext(UserContext);
    const {
        inForm,
        darkMode,
        setDarkMode,
        showWelcomeTutorial,
        setShowWelcomeTutorial,
    } = useContext(AppCtx);
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };
    const {pages} = useFlags();
    const {my_progress_page_active, guidance_page_active} = pages;
    const masquerade = sessionStorage.getItem('admin.masquerade');
    const isLargeViewport = isWidthUp('lg', width);

    const reset = () => {
        sessionStorage.removeItem('admin.masquerade');
        history.go(0);
    };

    const firstNavDiv = [
        {name: 'Home', to: '/home', icon: <MenuBookIcon />},
        {
            name: 'Sea Service',
            to: '/seaservice',
            icon: <AnchorGray {...iconProps} />,
        },
        {
            name: guidance_page_active ? 'License Guidance' : 'deactivated',
            to: '/guidance',
            icon: <AssignmentTurnedInOutlined />,
        },
        {
            name: my_progress_page_active ? 'My Progress' : 'deactivated',
            to: '/progress',
            icon: <AssignmentTurnedInOutlined />,
        },
        {
            name: 'My Documents',
            to: '/documents',
            icon: <FolderOutlined />,
        },
    ];

    const adminNavDiv = [{name: 'Admin', to: '/admin', icon: <SupervisorAccount />}];

    if (permissions?.canViewPayment) {
        adminNavDiv.push({
            name: 'Accounting',
            to: '/accounting',
            icon: <AccountBalance />,
        });
    }

    const secondNavDiv = [
        {
            name: 'Create CG719S',
            to: '/addtrackedss',
            icon: <DirectionsBoat />,
        },
    ];

    const thirdNavDiv = [
        {
            name: 'My Profile',
            to: '/profile',
            icon: <AccountCircleIcon {...iconProps} />,
        },
        {
            name: 'Settings',
            to: '/settings',
            icon: <SettingsOutlined />,
        },
        {
            name: 'Toggle Theme',
            onClick: () => setDarkMode(!darkMode),
            title: 'Toggle Light/Dark Mode',
            icon: darkMode ? (
                <Brightness4Outlined />
            ) : (
                <Brightness4 style={{color: 'gray'}} />
            ),
            component: ListItemButton,
        },
        {
            name: local ? 'Toggle Pro' : 'deactivated',
            onClick: togglePro,
            title: 'Toggle Pro / Free User',
            icon: subType === 'pro' ? 'PRO' : 'FREE',
            component: ListItemButton,
        },
    ];

    const fourthNavDiv = [
        {
            name: 'Welcome Tutorial',
            onClick: () => setShowWelcomeTutorial(!showWelcomeTutorial),
            icon: <DvrIcon />,
            component: ListItemButton,
        },

        {
            name: 'Logout',
            onClick: logOut,
            icon: <ClearRounded />,
        },
    ];

    const drawer = [
        <Greeting key='greeting' />,
        <Divider key='greeting-divider' />,
        <Show condition={permissions?.canViewUsers || masquerade} key='admin-items'>
            <NavItemList key='admin-list' name='admin-list' itemList={adminNavDiv} />
        </Show>,
        <PortalNavItems key='portal-nav-items' />,
        <NavItemList itemList={firstNavDiv} key='docs-list' name='docs-list' />,
        <NavItemList itemList={secondNavDiv} key='tools-list' />,
        <NavItemList
            itemList={thirdNavDiv}
            key='settings-list'
            name='settings-list'
        />,

        <NavItemList itemList={fourthNavDiv} key='common-list' />,
        <Grid
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            key='support-div'
            name='support-list'
            spacing={2}
        >
            <Grid item>
                <FixedSupport />
                <OnboardContent hideButton />
            </Grid>
        </Grid>,
    ];

    const scrollToTop = () => {
        // This it outside of react so no other way to do this
        document.querySelector('#root').scrollIntoView();
    };

    return [
        <AppBar position='fixed' className={classes.appBar} key='appbar'>
            <Toolbar onDoubleClick={scrollToTop}>
                <IconButton
                    color='inherit'
                    aria-label='open drawer'
                    edge='start'
                    onClick={handleDrawerToggle}
                    className={classes.menuButton}
                >
                    <MenuIcon />
                </IconButton>
                <Container maxWidth='xl' className={classes.mainBar}>
                    <Box display='flex' alignItems='center'>
                        <Hidden smUp>
                            <Box flexGrow={1} textAlign='center'>
                                <Link to='/'>
                                    <Logo height='35' width='22' />
                                </Link>
                            </Box>
                        </Hidden>
                        <Hidden xsDown>
                            <Box flexGrow={1}>
                                <Link to='/'>
                                    <LogoText height='35' width='180' />
                                </Link>
                            </Box>
                        </Hidden>
                        {masquerade ? (
                            <Button
                                variant='contained'
                                title={`Acting as ${masquerade}. Click to reset`}
                                onClick={reset}
                                style={{marginRight: '1em'}}
                                startIcon={<FaceIcon />}
                            >
                                <Hidden smDown>Acting as {masquerade}</Hidden>
                            </Button>
                        ) : null}
                        <Box>
                            {inForm ? (
                                <FixedSupport size='medium' rounded={false} />
                            ) : (
                                <Button
                                    component={Link}
                                    to='/adddocs'
                                    variant='contained'
                                    color='secondary'
                                >
                                    + Add
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Container>
            </Toolbar>
        </AppBar>,
        <nav className={classes.drawer} aria-label='main navigation' key='navbar'>
            <Drawer
                container={isLargeViewport ? null : props.window}
                variant={isLargeViewport ? 'permanent' : 'temporary'}
                anchor={theme.direction === 'rtl' ? 'right' : 'left'}
                open={isLargeViewport ? true : mobileOpen}
                onClose={handleDrawerToggle}
                classes={{
                    paper: classes.drawerPaper,
                }}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
            >
                {drawer}
            </Drawer>
        </nav>,
    ];
};

export default withWidth()(LoggedInNavBar);
export {LoggedInNavBar, NavItemList};
